<template>
  <section>
    <div class="support-page">
      <!-- Hero Section -->
      <section class="support-hero">
        <div class="support-hero__bg"></div>
        <div class="support-hero__overlay"></div>
        <div class="support-hero__content">
          <span class="support-hero__eyebrow">Golden Key Portal</span>
          <h1 class="support-hero__title">Como podemos ajudar?</h1>
          <p class="support-hero__subtitle">
            Envie o seu pedido de suporte e a nossa equipa entrará em contacto
            consigo.
          </p>

          <div class="support-hero__cta">
            <a href="#support-form" class="btn-primary">
              <q-icon name="support_agent" size="20px" />
              Criar pedido de suporte
            </a>
          </div>
        </div>
      </section>

      <!-- Form Section -->
      <section class="section-pad">
        <div class="support-wrapper" id="support-form">
          <div class="support-card">
            <div class="section-label">Formulário</div>
            <h2 class="section-heading">Criar pedido de suporte</h2>
            <p class="section-sub">
              Preencha os dados abaixo e responderemos o mais rapidamente
              possível.
            </p>

            <!-- Success Message -->
            <div v-if="successMessage" class="success-message">
              <div class="success-message__icon">
                <q-icon name="check_circle" size="32px" />
              </div>
              <h3>Pedido enviado com sucesso!</h3>
              <p>
                Obrigado pelo seu contacto. A nossa equipa irá analisar o seu
                pedido e entrar em contacto consigo brevemente.
              </p>
            </div>

            <!-- Form -->
            <q-form
              v-if="!successMessage"
              @submit="submitTicket"
              class="q-gutter-md"
            >
              <div class="form-grid">
                <div class="form-group">
                  <label>Nome *</label>
                  <q-input
                    v-model="ticket.name"
                    placeholder="Digite o seu nome"
                    outlined
                    dense
                    class="form-input"
                    :rules="[(val) => !!val || 'Nome é obrigatório']"
                  >
                    <template v-slot:prepend>
                      <q-icon name="person" size="18px" />
                    </template>
                  </q-input>
                </div>

                <div class="form-group">
                  <label>Email *</label>
                  <q-input
                    v-model="ticket.email"
                    placeholder="Digite o seu email"
                    outlined
                    dense
                    class="form-input"
                    type="email"
                    :rules="[
                      (val) => !!val || 'Email é obrigatório',
                      (val) => /.+@.+\..+/.test(val) || 'Email inválido',
                    ]"
                  >
                    <template v-slot:prepend>
                      <q-icon name="email" size="18px" />
                    </template>
                  </q-input>
                </div>

                <div class="form-group">
                  <label>Celular *</label>
                  <q-input
                    v-model="ticket.phone"
                    placeholder="+258 84 123 4567"
                    mask="+258 ## ### ####"
                    outlined
                    dense
                    class="form-input"
                    :rules="[
                      (val) =>
                        !!val || val.length >= 9 || 'Celular é obrigatório',
                    ]"
                  >
                    <template v-slot:prepend>
                      <q-icon name="phone" size="18px" />
                    </template>
                  </q-input>
                </div>

                <div class="form-group">
                  <label>Assunto *</label>
                  <q-select
                    v-model="ticket.subject"
                    :options="opcoes"
                    placeholder="Selecione uma opção"
                    outlined
                    dense
                    class="form-input"
                    lazy-rules
                    emit-value
                    map-options
                  >
                    <template v-slot:prepend>
                      <q-icon name="title" size="18px" />
                    </template>
                  </q-select>
                </div>
              </div>

              <div class="form-group">
                <label>Descrição *</label>
                <q-input
                  v-model="ticket.description"
                  placeholder="Ex: Aplicação não carrega imagens"
                  outlined
                  dense
                  class="form-input"
                  type="textarea"
                  rows="6"
                  :rules="[(val) => !!val || 'Descrição é obrigatória']"
                >
                  <template v-slot:prepend>
                    <q-icon name="description" size="18px" />
                  </template>
                </q-input>
              </div>
              <div class="form-group">
                <label>Imagem do erro</label>
                <q-file
                  v-model="ticket.file"
                  label="Selecionar imagens (máx. 5, 10MB cada)"
                  outlined
                  dense
                  multiple
                  counter
                  max-files="5"
                  max-file-size="10485760"
                  accept=".jpg,.jpeg,.png,image/*"
                  @rejected="onRejected"
                >
                  <template v-slot:prepend>
                    <q-icon name="attach_file" size="18px" />
                  </template>
                </q-file>
              </div>

              <div class="btn-wrapper">
                <q-btn
                  type="submit"
                  class="btn-submit"
                  label="Enviar pedido de suporte"
                  icon="send"
                  :loading="submitting"
                  :disable="submitting"
                  no-caps
                />
              </div>

              <div class="form-footer">
                <q-icon name="lock" size="14px" />
                Os seus dados são utilizados apenas para suporte.
              </div>
            </q-form>
          </div>

          <!-- Side Panel -->
          <aside class="support-aside">
            <div class="info-card">
              <div class="section-label">Informação</div>
              <h3 class="info-card__title">Informações úteis</h3>

              <div class="info-item">
                <div class="info-item__icon info-item__icon--gold">
                  <q-icon name="schedule" size="22px" />
                </div>
                <div class="info-item__body">
                  <strong>Tempo de resposta</strong>
                  <p>Até 24 horas úteis.</p>
                </div>
              </div>

              <div class="info-item">
                <div class="info-item__icon info-item__icon--blue">
                  <q-icon name="mark_email_read" size="22px" />
                </div>
                <div class="info-item__body">
                  <strong>Contacto</strong>
                  <p>Entraremos em contacto através do email informado.</p>
                </div>
              </div>

              <div class="info-item">
                <div class="info-item__icon info-item__icon--green">
                  <q-icon name="lock" size="22px" />
                </div>
                <div class="info-item__body">
                  <strong>Privacidade</strong>
                  <p>
                    Os seus dados são protegidos e utilizados apenas para
                    suporte.
                  </p>
                </div>
              </div>
            </div>

            <div class="info-card">
              <div class="section-label">Contactos</div>
              <h3 class="info-card__title">Fale connosco</h3>

              <div class="info-item">
                <div class="info-item__icon info-item__icon--blue">
                  <q-icon name="email" size="22px" />
                </div>
                <div class="info-item__body">
                  <strong>Email</strong>
                  <p>comercial@goldenkey.co.mz</p>
                </div>
              </div>

              <div class="info-item">
                <div class="info-item__icon info-item__icon--blue">
                  <q-icon name="phone" size="22px" />
                </div>
                <div class="info-item__body">
                  <strong>Telefone</strong>
                  <p>+258 82 701 6716</p>
                </div>
              </div>

              <div class="info-item">
                <div class="info-item__icon info-item__icon--gold">
                  <q-icon name="access_time" size="22px" />
                </div>
                <div class="info-item__body">
                  <strong>Horário</strong>
                  <p>Seg – Sex: 8h às 18h · Sáb: 9h às 14h</p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  </section>
</template>

<script setup>
import { reactive, ref } from "vue";
import { useRouter } from "vue-router";
import store from "@/vuex/store";
import Swal from "sweetalert2";

const successMessage = ref(false);
const submitting = ref(false);
const router = useRouter();

const opcoes = [
  {
    label: "Selecione uma opção",
    value: "",
    disable: true,
  },
  {
    label: "Desejo publicar um banner de publicidade",
    value: "Desejo publicar um banner de publicidade",
  },
  {
    label: "Estou enfrentando problemas com login",
    value: "Estou enfrentando problemas com login",
  },
  {
    label: "Estou enfrentando problemas com registo",
    value: "Estou enfrentando problemas com registo",
  },
  {
    label: "Como contactar um agente",
    value: "Como contactar um agente",
  },
  { label: "Outro", value: "Outro" },
];

function categorizarFrase(frase) {
  const fraseNormalizada = frase.toLowerCase();

  const wordlistBilling = [];

  const wordlistTechnical = [
    "estou enfrentando problemas com login",
    "estou enfrentando problemas com registo",
    "como contactar um agente",
  ];

  const wordlistFeatureRequest = ["desejo publicar um banner de publicidade"];

  const categorias = [
    { nome: "billing", lista: wordlistBilling, prioridade: "high" },
    { nome: "technical", lista: wordlistTechnical, prioridade: "medium" },
    {
      nome: "feature-request",
      lista: wordlistFeatureRequest,
      prioridade: "medium",
    },
  ];

  for (const categoria of categorias) {
    const encontrado = categoria.lista.some((termo) =>
      fraseNormalizada.includes(termo),
    );
    if (encontrado) {
      return { categoria: categoria.nome, prioridade: categoria.prioridade };
    }
  }

  return { categoria: "general", prioridade: "low" };
}

const onRejected = (rejectedEntries) => {
  Swal.fire({
    icon: "error",
    title: "Ficheiro Rejeitado",
    text: `${rejectedEntries.length} ficheiro(s) não cumprem os requisitos de tamanho ou quantidade.`,
    confirmButtonColor: "#1976D2", // Cor primária do Quasar para combinar
  });
};

const ticket = reactive({
  name: "",
  email: "",
  phone: "",
  subject: "",
  description: "",
  file: null,
});

const storedSubject = localStorage.getItem("supportSubject");
if (storedSubject === "anunciar") {
  ticket.subject = "Desejo publicar um banner de publicidade";
  localStorage.removeItem("supportSubject");
}

const resetForm = () => {
  (ticket.description = ""),
    (ticket.subject = ""),
    (ticket.phone = ""),
    (ticket.name = ""),
    (ticket.file = null),
    (ticket.email = "");
};

async function submitTicket() {
  submitting.value = true;

  if (!ticket.subject) {
    ticket.subject = "Outro";
  }
  const resultado = categorizarFrase(ticket.subject);
  const phoneE164 = ticket.phone.replace(/\s+/g, "");

  const formData = new FormData();
  formData.append("title", `${ticket.subject} - GoldenKey`);
  formData.append("client_name", ticket.name);
  formData.append("client_email", ticket.email);
  formData.append("client_phone", phoneE164);
  formData.append("category", resultado.categoria);
  formData.append("priority", resultado.prioridade);
  formData.append("description", ticket.description);

  if (ticket.file && ticket.file.length) {
    ticket.file.forEach((file) => {
      formData.append("files[]", file, file.name);
    });
  }

  try {
    await store.dispatch("support/createTicket", formData);

    Swal.fire({
      title: "Sucesso!",
      text: "Pedido enviado com sucesso. \n O seu pedido está sendo acompanhado por nossa equipe.\n Brevemente Entraremos em Contacto.",
      icon: "success",
      confirmButtonText: "OK",
    }).then(() => {
      router.push("/suporte");
    });

    resetForm();
  } catch (err) {
    console.error(err);
    Swal.fire({
      title: "Erro!",
      text: "Houve um problema ao enviar o pedido. Tente novamente.",
      icon: "error",
      confirmButtonText: "OK",
    });
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap");
/* ══════════════════════════════════════════════════
   Smooth Scroll
══════════════════════════════════════════════════ */
html {
  scroll-behavior: smooth;
}

/* Opcional: Ajusta a posição de destino para compensar o header fixo */
#support-form {
  scroll-margin-top: 80px;
}
/* ══════════════════════════════════════════════════
   Base
══════════════════════════════════════════════════ */
.support-page {
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  background: #f8fafc;
  min-height: 100vh;
}

.section-pad {
  padding: 5rem 0 6rem;
}

/* ── Section typography ─────────────────── */
.section-label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #eba301;
  margin-bottom: 0.75rem;
}

.section-heading {
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  font-weight: 300;
  color: #0f172a;
  letter-spacing: -0.01em;
  line-height: 1.2;
  margin: 0 0 0.5rem;
}

.section-sub {
  font-size: 1rem;
  font-weight: 400;
  color: #64748b;
  margin: 0 0 2.5rem;
  line-height: 1.6;
}

/* ══════════════════════════════════════════════════
   Hero
══════════════════════════════════════════════════ */
.support-hero {
  position: relative;
  width: 100%;
  height: 52vh;
  min-height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.support-hero__bg {
  position: absolute;
  inset: 0;
  background-image: url("https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2000&q=80");
  background-size: cover;
  background-position: center;
}

.support-hero__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(8, 12, 24, 0.88) 0%,
    rgba(8, 12, 24, 0.55) 40%,
    rgba(8, 12, 24, 0.3) 100%
  );
}

.support-hero__content {
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 0 2rem;
  max-width: 820px;
  animation: fadeUp 0.9s ease-out both;
}

.support-hero__eyebrow {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #eba301;
  margin-bottom: 1rem;
}

.support-hero__title {
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: clamp(2.25rem, 5vw, 3.75rem);
  font-weight: 300;
  color: #ffffff;
  letter-spacing: 0.02em;
  line-height: 1.1;
  margin: 0 0 1rem;
  text-shadow: 0 4px 24px rgba(0, 0, 0, 0.35);
}

.support-hero__subtitle {
  font-size: 1.05rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.6;
  margin: 0 0 2rem;
}

.support-hero__cta {
  display: flex;
  justify-content: center;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  background: #eba301;
  color: #ffffff;
  padding: 0.95rem 1.85rem;
  border-radius: 12px;
  text-decoration: none;
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: 0.01em;
  transition: transform 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
  box-shadow: 0 10px 30px rgba(235, 163, 1, 0.32);
}

.btn-primary:hover {
  transform: translateY(-2px);
  background: #d49401;
  box-shadow: 0 14px 34px rgba(235, 163, 1, 0.4);
}

/* ══════════════════════════════════════════════════
   Form card
══════════════════════════════════════════════════ */
.support-card {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 14px;
  padding: 2.5rem;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.06);
}

/* 1. Permite que os itens do grid encolham abaixo do seu conteúdo mínimo */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem 1.5rem;
  min-width: 0; /* opcional mas seguro */
}

.form-group {
  margin-bottom: 0.5rem;
  display: flex;
  flex-direction: column;
  min-width: 0; /* 👈 CRÍTICO: impede que o conteúdo force a largura da coluna */
}

.form-input {
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  min-width: 0; /* 👈 idem */
}

/* Também no wrapper geral, por segurança */
.support-wrapper {
  width: 90%;
  max-width: 1200px;
  margin: -60px auto 0;
  position: relative;
  z-index: 3;
  display: grid;
  grid-template-columns: 1.8fr 1fr;
  gap: 1.75rem;
  align-items: start;
  min-width: 0;
}

/* 2. Impede que o texto selecionado force largura mínima grande */
.form-input :deep(.q-field__native) {
  font-size: 0.95rem;
  color: #0f172a;
  padding: 0.65rem 0.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.form-input :deep(.q-field__control) {
  min-width: 0;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: -0.005em;
}

/* Quasar Input Overrides */
.form-input :deep(.q-field__control:hover) {
  background: #ffffff !important;
}

.form-input :deep(.q-field__control:focus-within) {
  background: #ffffff !important;
  border-color: #eba301 !important;
  box-shadow: 0 0 0 4px rgba(235, 163, 1, 0.12) !important;
}

.form-input :deep(.q-field__native)::placeholder {
  color: #94a3b8;
}

.form-input :deep(.q-field__prepend) {
  color: #94a3b8;
  padding-right: 0.25rem;
}

.form-input :deep(.q-field__prepend .q-icon) {
  font-size: 18px;
}

.form-input :deep(.q-field--focused .q-field__prepend) {
  color: #eba301;
}

/* Textarea specific */
.form-input :deep(.q-field--textarea .q-field__native) {
  min-height: 170px;
  line-height: 1.6;
  padding: 0.75rem 0.5rem;
}

/* Error state */
.form-input :deep(.q-field--error .q-field__control) {
  border-color: #c62828 !important;
}

.form-input :deep(.q-field--error .q-field__control:focus-within) {
  box-shadow: 0 0 0 4px rgba(198, 40, 40, 0.12) !important;
}

/* Select specific */
.form-input :deep(.q-select) {
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
}

/* ══════════════════════════════════════════════════
   Submit Button
══════════════════════════════════════════════════ */
.btn-submit {
  width: 100%;
  margin-top: 0.5rem;
  border: none;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  background: #eba301;
  color: #ffffff;
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 0.98rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  transition: transform 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
  box-shadow: 0 10px 26px rgba(235, 163, 1, 0.28);
}

.btn-submit:hover {
  transform: translateY(-2px);
  background: #d49401;
  box-shadow: 0 14px 32px rgba(235, 163, 1, 0.38);
}

.btn-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none !important;
}

/* Quasar button override */
.btn-submit :deep(.q-btn) {
  background: #eba301 !important;
  color: #ffffff !important;
  font-family: "Plus Jakarta Sans", system-ui, sans-serif !important;
  font-weight: 600 !important;
  font-size: 0.98rem !important;
  padding: 1rem 1.5rem !important;
  border-radius: 12px !important;
  text-transform: none !important;
  letter-spacing: 0.01em !important;
  box-shadow: 0 10px 26px rgba(235, 163, 1, 0.28) !important;
}

.btn-submit :deep(.q-btn:hover) {
  background: #d49401 !important;
  box-shadow: 0 14px 32px rgba(235, 163, 1, 0.38) !important;
  transform: translateY(-2px);
}

.btn-submit :deep(.q-btn__content) {
  gap: 0.6rem;
}

/* ══════════════════════════════════════════════════
   Form Footer
══════════════════════════════════════════════════ */
.form-footer {
  margin-top: 1.25rem;
  text-align: center;
  color: #94a3b8;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
}

/* ══════════════════════════════════════════════════
   Success
══════════════════════════════════════════════════ */
.success-message {
  background: rgba(33, 186, 69, 0.06);
  border: 1px solid rgba(33, 186, 69, 0.22);
  padding: 2.25rem 1.75rem;
  border-radius: 14px;
  text-align: center;
}

.success-message__icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(33, 186, 69, 0.12);
  color: #21ba45;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.success-message h3 {
  color: #0f172a;
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0 0 0.6rem;
}

.success-message p {
  color: #64748b;
  line-height: 1.7;
  font-size: 0.95rem;
  margin: 0;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
}

/* ══════════════════════════════════════════════════
   Side panel
══════════════════════════════════════════════════ */
.support-aside {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.info-card {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 14px;
  padding: 2rem 1.75rem;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.05);
}

.info-card__title {
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 1.2rem;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: -0.01em;
  margin: 0 0 1.5rem;
}

.info-item {
  display: flex;
  gap: 0.9rem;
  align-items: flex-start;
  padding: 0.65rem 0;
}

.info-item + .info-item {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  margin-top: 0.25rem;
  padding-top: 1rem;
}

.info-item__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.info-item__icon--gold {
  background: rgba(235, 163, 1, 0.1);
  color: #eba301;
}

.info-item__icon--blue {
  background: rgba(2, 123, 227, 0.09);
  color: #027be3;
}

.info-item__icon--green {
  background: rgba(33, 186, 69, 0.09);
  color: #21ba45;
}

.info-item__body strong {
  display: block;
  margin-bottom: 0.25rem;
  color: #0f172a;
  font-size: 0.92rem;
  font-weight: 600;
}

.info-item__body p {
  color: #64748b;
  line-height: 1.6;
  font-size: 0.87rem;
  margin: 0;
}

/* ══════════════════════════════════════════════════
   Animation
══════════════════════════════════════════════════ */
@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ══════════════════════════════════════════════════
   Responsive
══════════════════════════════════════════════════ */
@media (max-width: 1024px) {
  .support-wrapper {
    grid-template-columns: 1fr;
    margin-top: -50px;
  }

  .support-hero {
    height: 46vh;
    min-height: 380px;
  }
}

@media (max-width: 768px) {
  .support-hero {
    height: 46vh;
    min-height: 340px;
  }

  .support-hero__title {
    font-size: clamp(1.75rem, 7vw, 2.25rem);
  }

  .support-hero__subtitle {
    font-size: 0.92rem;
    padding: 0 0.5rem;
  }

  .support-hero__eyebrow {
    font-size: 0.55rem;
  }

  .btn-primary {
    padding: 0.75rem 1.5rem;
    font-size: 0.85rem;
  }

  .support-card {
    padding: 1.75rem 1.25rem;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .section-pad {
    padding: 3rem 0 4rem;
  }

  .support-wrapper {
    width: 92%;
    margin-top: -40px;
  }

  .support-hero__cta .btn-primary .q-icon {
    display: none;
  }
}

@media (max-width: 480px) {
  .support-hero {
    height: 40vh;
    min-height: 280px;
  }

  .support-hero__content {
    padding: 0 1rem;
  }

  .support-hero__title {
    font-size: clamp(1.5rem, 8vw, 1.75rem);
  }

  .support-hero__subtitle {
    font-size: 0.85rem;
  }

  .support-wrapper {
    width: 95%;
    margin-top: -30px;
  }

  .support-card {
    padding: 1.25rem 0.75rem;
  }

  .section-heading {
    font-size: 1.4rem;
  }

  .section-sub {
    font-size: 0.9rem;
  }

  .info-card {
    padding: 1.5rem 1rem;
  }
}
</style>
